<template>
  <div
    id="notification-list"
    class="z-80000"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <transition-group
      tag="div"
      name="list-complete"
      class="pointer-events-none fixed flex items-start top-0 w-full p-1 md:p-6 flex-col z-80000"
    >
      <div
        v-for="item in items"
        :key="item.key"
        class="list-complete-item pointer-events-auto rounded-md shadow-md z-80000 max-w-full relative mb-4 mx-auto bg-white/85 backdrop-blur border dark:bg-gray-900"
        :class="item.width"
      >
        <div class="flex items-center justify-between w-full">
          <!--<div v-if="item.icon" class="p-4 pr-0">
            <o-icon :icon="item.icon" :class="item.variant" :size="22" />
          </div>-->

          <div class="p-4 flex flex-col space-y-1 pr-8">
            <div
              :class="[
                'text-base font-medium leading-none',
                item.variant
              ]"
            >
              {{ item.title }}
            </div>
            <div v-if="item.text" class="text-sm text-gray-700 leading-tight dark:text-gray-200">
              {{ item.text }}
            </div>

            <div v-if="item.content" class="mt-3">
              <component :is="item.content" />
            </div>
          </div>

          <div class="flex items-center space-x-2 pr-2">
            <div v-if="item.action">
              <o-button flat size="xs" @click="handleActionClick(item)">
                {{ item.action.label }}
              </o-button>
            </div>

            <button
              class="text-black dark:text-white opacity-70 p-1 bg-transparent"
              @click="close(item)"
            >
              <o-icon icon="x" size="16" />
            </button>
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import {
  OInternalLink,
} from '@/components/elements'

const { $i18n } = useNuxtApp()
const key = () => `${Date.now()}-${Math.random()}`

const items = ref<any[]>([])
const parent = ref(false)

function open({ title, text, icon, variant, action, duration, content, width = 'w-100' }: any) {
  if (!parent.value) {
    const instance = getCurrentInstance()
    if (instance) {
      // Mount happens automatically in setup context
      parent.value = true
    }
  }
  return new Promise((resolve) => {
    const item: any = {
      key: key(),
      show: true,
      text,
      title,
      action,
      icon,
      variant: getVariant(variant),
      duration,
      content,
      width,
      resolve,
      timer: new Timer(() => {
        close(item)
      }, duration),
    }
    items.value.push(item)
  })
}

function toast(options: any = {}) {
  const { title, text, icon, variant, action, duration = 5000, content, width = 'w-100' } = options
  return open({ title, text, icon, variant, action, duration, content, width })
}

function error(options: any = {}) {
  const { title = $i18n.t('components.elements.notification.error'), text = $i18n.t('components.elements.notification.errorInfo'), icon, errors, variant = 'danger', duration = 10000 } = options
  return open({ title, text: errors ? Object.values(errors).join(', ') : text, icon, variant, duration })
}

function remove(item: any) {
  const i = items.value.indexOf(item)
  if (i >= 0) {
    items.value.splice(i, 1)
  }
}

function close(item: any, ok?: boolean) {
  item.resolve()
  item.show = false
  remove(item)
}

function getIcon(variant: string) {
  switch (variant) {
    case 'success':
      return 'success'
    case 'danger':
      return 'danger'
    case 'warning':
      return 'warning'
    case 'info':
      return 'info'
    default:
      return 'notification'
  }
}

function getVariant(variant: string) {
  switch (variant) {
    case 'success':
      return 'text-green-500 dark:text-green-600'
    case 'danger':
      return 'text-red-500 dark:text-red-500'
    case 'warning':
      return 'text-yellow-500 dark:text-yellow-500'
    case 'info':
      return 'text-blue-500 dark:text-blue-500'
    default:
      return 'text-gray-800 dark:text-gray-100'
  }
}

class Timer {
  private timerId: ReturnType<typeof setTimeout> | undefined
  private start: number = 0
  private remaining: number
  private callback: () => void

  constructor(callback: () => void, delay: number) {
    this.callback = callback
    this.remaining = delay
    this.resume()
  }

  pause() {
    window.clearTimeout(this.timerId)
    this.remaining -= Date.now() - this.start
  }

  resume() {
    this.start = Date.now()
    window.clearTimeout(this.timerId)
    this.timerId = window.setTimeout(this.callback, this.remaining)
  }
}

function handleMouseEnter() {
  items.value.forEach((notification) => {
    notification.timer.pause()
  })
}

function handleMouseLeave() {
  items.value.forEach((notification) => {
    notification.timer.resume()
  })
}

function handleActionClick(item: any) {
  item.action.click()
  close(item, true)
}

defineExpose({ open, toast, error, close, remove })
</script>

<style lang="scss">
  .list-complete-item {
    transition: all 0.5s;
    display: inline-flex;
    margin-right: 10px;
  }
  .list-complete-enter-from, .list-complete-leave-to {
    opacity: 0;
    transform: translateY(-40px);
  }
  .list-complete-leave-active {
    position: absolute;
  }
</style>
